import Icon from '../ui/Icon';
import StatusBadge from '../ui/StatusBadge';
import { copyText } from '../../utils/clipboard';

const TH = 'py-3 px-4 text-left text-[11px] leading-[14px] font-semibold text-on-surface-variant uppercase tracking-wider whitespace-nowrap';

export function CategoryBadge({ category }) {
  return (
    <span className={`px-1.5 py-0.5 rounded-full text-[10px] uppercase font-bold whitespace-nowrap ${category.badge}`}>{category.label}</span>
  );
}

/** Website visitor without a Pompi account. */
export function GuestBadge() {
  return <span className="px-1.5 py-0.5 rounded-full text-[10px] uppercase font-bold whitespace-nowrap shrink-0 bg-surface-container-high text-on-surface-variant">Guest</span>;
}

/** One row per ticket; clicking it (or "Read More") opens the reply panel. */
export default function FeedbackTable({ rows, selectedId, refreshing, onOpen }) {
  return (
    <div className="w-full overflow-x-auto" aria-busy={refreshing}>
      <table className="w-full min-w-[860px] text-left border-collapse">
        <caption className="sr-only">Customer feedback</caption>
        <thead>
          <tr className="bg-surface-container-low border-b border-surface-container">
            <th scope="col" className={`${TH} w-12`}>
              #
            </th>
            <th scope="col" className={TH}>
              Customer
            </th>
            <th scope="col" className={TH}>
              Subject &amp; Category
            </th>
            <th scope="col" className={TH}>
              Message Preview
            </th>
            <th scope="col" className={TH}>
              Status
            </th>
          </tr>
        </thead>
        <tbody className={`divide-y divide-surface-container transition-opacity ${refreshing ? 'opacity-50' : ''}`}>
          {rows.map((row) => {
            const selected = row.id === selectedId;
            return (
              <tr
                key={row.id}
                onClick={() => onOpen(row.id)}
                aria-current={selected || undefined}
                className={`cursor-pointer transition-colors ${selected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-surface-container-low/60'}`}
              >
                <td className="py-3.5 px-4 align-top font-data-mono text-[13px] text-on-surface-variant">{row.position}</td>
                <td className="py-3.5 px-4 align-top">
                  <div className="flex items-start gap-3 min-w-[220px]">
                    <span
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${row.customer.avatarClass}`}
                      aria-hidden="true"
                    >
                      {row.customer.initials}
                    </span>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-[13px] font-semibold text-on-surface">
                        <span className="truncate">{row.customer.name}</span>
                        {row.customer.isGuest && <GuestBadge />}
                      </p>
                      {row.customer.email && (
                        <p className="flex items-center gap-1 text-xs text-on-surface-variant">
                          <span className="font-data-mono truncate max-w-[200px]" title={row.customer.email}>
                            {row.customer.email}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyText(row.customer.email, 'Email copied');
                            }}
                            aria-label={`Copy email ${row.customer.email}`}
                            title="Copy email"
                            className="p-0.5 rounded text-on-surface-variant hover:text-primary outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
                          >
                            <Icon name="content_copy" size={14} />
                          </button>
                        </p>
                      )}
                      {row.customer.phone && <p className="font-data-mono text-[11px] text-on-surface-variant">{row.customer.phone}</p>}
                    </div>
                  </div>
                </td>
                <td className="py-3.5 px-4 align-top">
                  <div className="min-w-[200px] max-w-[300px]">
                    <p className="text-[13px] font-semibold text-on-surface line-clamp-2">{row.subject}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <CategoryBadge category={row.category} />
                      <span className="font-data-mono text-[11px] text-on-surface-variant whitespace-nowrap">Ticket #{row.ticketNo}</span>
                      {row.source && (
                        <span className="inline-flex items-center gap-1 font-data-mono text-[11px] text-on-surface-variant whitespace-nowrap" title="Source">
                          <Icon name={row.source.icon} size={14} />
                          {row.source.label}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-3.5 px-4 align-top">
                  <div className="min-w-[180px] max-w-[280px]">
                    <p className="text-xs text-on-surface-variant line-clamp-2 break-words">{row.preview ? `“${row.preview}”` : '—'}</p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(row.id);
                      }}
                      aria-label={`Read more of ticket ${row.ticketNo}`}
                      className="text-secondary text-[11px] font-semibold hover:underline mt-0.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
                    >
                      Read More
                    </button>
                  </div>
                </td>
                <td className="py-3.5 px-4 align-top">
                  <div className="flex flex-col items-start gap-1">
                    <StatusBadge tone={row.status.tone}>{row.status.label}</StatusBadge>
                    <span className="flex items-center gap-1.5 text-[11px] text-on-surface-variant whitespace-nowrap" title={row.createdExact}>
                      {row.createdAgo}
                      {row.attachmentCount > 0 && (
                        <span className="inline-flex items-center" title={`${row.attachmentCount} attachment${row.attachmentCount === 1 ? '' : 's'}`}>
                          <Icon name="attach_file" size={14} />
                          <span className="sr-only">{row.attachmentCount} attachments</span>
                        </span>
                      )}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
